import { api, API_BASE } from "@/lib/apiClient";

export type InvoiceMailStatus = "pending" | "confirmed" | "dismissed" | "skipped";

export interface InvoiceMailMatchTarget {
  bestellnummer?: string | null;
  auftrag_id?: number | null;
  order_id?: number | null;
  auftragsnummer?: string | null;
  jahr?: number | null;
  sharepoint_folder_id?: string | null;
  auftrag_beschreibung?: string | null;
  supplier?: string | null;
  kunde?: string | null;
  subfolder?: string | null;
  target_path_hint?: string | null;
  match_confidence?: "exact" | "partial" | "fuzzy" | null;
  matched_fragment?: string | null;
  match_kind?: "bestellung" | "projekt" | null;
}

export interface InvoiceMailBestellnummerSearchItem {
  bestellnummer?: string | null;
  auftrag_id?: number | null;
  auftragsnummer?: string | null;
  jahr?: number | null;
  auftrag_beschreibung?: string | null;
  sharepoint_folder_id?: string | null;
  supplier?: string | null;
  kunde?: string | null;
  subfolder?: string | null;
  score?: number | null;
  match_kind?: "bestellung" | "projekt" | null;
  match_confidence?: "exact" | "partial" | "fuzzy" | null;
  matched_fragment?: string | null;
}

export interface InvoiceMailAttachmentSummary {
  id?: string | null;
  name?: string | null;
  size?: number | null;
  contentType?: string | null;
  odata_type?: string | null;
  matched_tks?: string[] | null;
  einvoice_parsed?: boolean;
  einvoice_line_count?: number;
  einvoice_format?: string | null;
  einvoice_status?: string | null;
  is_inline?: boolean | null;
  isInline?: boolean | null;
  merged?: boolean | null;
  merged_from?: string[] | null;
  merged_source?: boolean | null;
}

export const MERGED_INVOICE_ATTACHMENT_ID = "merged";

export interface InvoiceMailCandidate {
  id: number;
  mailbox: string;
  graph_message_id?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  subject?: string | null;
  received_at?: string | null;
  snippet?: string | null;
  has_attachments?: boolean;
  attachments_summary?: InvoiceMailAttachmentSummary[] | null;
  matched_bestellnummern?: string[] | null;
  match_targets?: InvoiceMailMatchTarget[] | null;
  match_reasons?: string[] | null;
  status: InvoiceMailStatus;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  dismissed_at?: string | null;
  dismissed_by?: string | null;
  dismiss_note?: string | null;
  sharepoint_results?: {
    eml?: Array<{ path?: string; web_url?: string; auftragsnummer?: string }>;
    attachments?: Array<{ path?: string; web_url?: string; filename?: string }>;
  } | null;
  approval?: {
    status: string;
    assignee_login?: string | null;
    assignee_name?: string | null;
    second_assignee_name?: string | null;
    participants?: Array<{ login: string; name?: string | null; decision?: string | null }>;
  } | null;
  assignment_suggestion?: {
    login?: string | null;
    name?: string | null;
    kuerzel?: string | null;
    kuerzels?: string[] | null;
    reason?: string | null;
  } | null;
}

export interface InvoiceMailListResponse {
  mailbox: string;
  default_subfolder: string;
  live_monitor?: boolean;
  items: InvoiceMailCandidate[];
  counts?: {
    total: number;
    with_attachments: number;
    without_attachments: number;
  };
}

export interface InvoiceMailConfirmPayload {
  sharepoint_targets: Array<{
    auftrag_id: number;
    bestellnummer?: string | null;
    subfolder?: string | null;
  }>;
  include_eml?: boolean;
  selected_attachment_ids?: string[] | null;
  move_in_mailbox_to?: string | null;
}

export async function listInvoiceMailCandidates(
  status?: InvoiceMailStatus,
): Promise<InvoiceMailListResponse> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return api.get<InvoiceMailListResponse>(`/invoice-mail/candidates${q}`);
}

export async function scanInvoiceMailbox(options?: {
  sinceDate?: string;
  days?: number;
  maxItems?: number;
}): Promise<{
  ok: boolean;
  queued: boolean;
  job_id?: string;
  message?: string;
  since_date?: string | null;
}> {
  const qs = new URLSearchParams();
  if (options?.sinceDate) qs.set("since_date", options.sinceDate);
  else if (options?.days != null) qs.set("days", String(options.days));
  if (options?.maxItems != null) qs.set("max_items", String(options.maxItems));
  const q = qs.toString();
  return api.post(q ? `/invoice-mail/scan?${q}` : "/invoice-mail/scan", {});
}

export type InvoiceMailScanJobStatus = "queued" | "running" | "completed" | "failed";

export interface InvoiceMailScanJob {
  job_id: string;
  status: InvoiceMailScanJobStatus;
  phase?: "queued" | "collecting" | "processing" | "done";
  mailbox?: string;
  since_date?: string | null;
  total?: number | null;
  processed?: number;
  matched?: number;
  skipped?: number;
  errors?: number;
  einvoice_parsed?: number;
  einvoice_lines?: number;
  progress_percent?: number;
  truncated?: boolean;
  message?: string | null;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export async function getInvoiceMailScanJob(jobId: string): Promise<InvoiceMailScanJob> {
  return api.get<InvoiceMailScanJob>(`/invoice-mail/scan/${encodeURIComponent(jobId)}`);
}

export const INVOICE_MAIL_SCAN_JOB_STORAGE_KEY = "invoiceMailScanJobId";

export async function searchInvoiceMailBestellnummern(
  q: string,
): Promise<InvoiceMailBestellnummerSearchItem[]> {
  const res = await api.get<{ items: InvoiceMailBestellnummerSearchItem[] }>(
    `/invoice-mail/bestellnummer-search?q=${encodeURIComponent(q)}`,
  );
  return res.items || [];
}

export async function reanalyzeInvoiceMailCandidate(
  id: number,
): Promise<{ ok: boolean; skipped?: boolean; einvoice_parsed?: boolean; einvoice_line_count?: number }> {
  return api.post(`/invoice-mail/candidates/${id}/reanalyze`, {});
}

export interface EingangsrechnungArtikelLine {
  id: number;
  parse_id: number;
  candidate_id: number;
  line_index: number;
  bezeichnung: string;
  beschreibung?: string | null;
  menge?: number | null;
  einheit?: string | null;
  einzelpreis_netto?: number | null;
  zeilen_netto?: number | null;
  rabatt_prozent?: number | null;
  mwst_satz?: number | null;
  seller_article_nr?: string | null;
  ean?: string | null;
  buyer_article_nr?: string | null;
  stamm_artikel_id?: number | null;
  match_method?: string | null;
  match_confidence?: string | null;
  lieferant_name?: string | null;
  stamm_artikel_bezeichnung?: string | null;
  stamm_hero_nr?: string | null;
  created_at?: string | null;
  candidate_subject?: string | null;
  candidate_from_name?: string | null;
  lieferant_geschaeftspartner_id?: number | null;
  lieferant_gp_id?: number | null;
  lieferant_firmenname?: string | null;
}

export interface InvoiceMailEinvoiceResponse {
  candidate_id: number;
  summary: {
    parse_count: number;
    successful_parses: number;
    line_count: number;
  };
  parses: Array<{
    id: number;
    attachment_name?: string | null;
    format?: string | null;
    parse_status?: string | null;
    invoice_number?: string | null;
    invoice_date?: string | null;
    supplier_name?: string | null;
    total_net?: number | null;
    total_gross?: number | null;
    currency?: string | null;
    error_message?: string | null;
  }>;
  lines: EingangsrechnungArtikelLine[];
}

export async function getInvoiceMailEinvoice(candidateId: number): Promise<InvoiceMailEinvoiceResponse> {
  return api.get<InvoiceMailEinvoiceResponse>(`/invoice-mail/candidates/${candidateId}/einvoice`);
}

export async function listEingangsrechnungArtikel(params?: {
  candidate_id?: number;
  lieferant_gp_id?: number;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: EingangsrechnungArtikelLine[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  if (params?.candidate_id != null) qs.set("candidate_id", String(params.candidate_id));
  if (params?.lieferant_gp_id != null) qs.set("lieferant_gp_id", String(params.lieferant_gp_id));
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  return api.get(q ? `/invoice-mail/eingangsrechnung-artikel?${q}` : "/invoice-mail/eingangsrechnung-artikel");
}

export interface EingangsrechnungImportResult {
  ok: boolean;
  created?: number;
  linked?: number;
  skipped?: number;
  results?: Array<{
    id: number;
    ok: boolean;
    stamm_artikel_id?: number;
    linked?: boolean;
    reason?: string;
    hero_nr?: string;
  }>;
}

export async function importEingangsrechnungArtikelToStamm(
  items: Array<{ id: number; verkaufspreis: number }>,
): Promise<EingangsrechnungImportResult> {
  return api.post<EingangsrechnungImportResult>(
    "/invoice-mail/eingangsrechnung-artikel/import-stamm",
    { items },
  );
}

export interface EingangsrechnungLinkLieferantResult {
  ok: boolean;
  updated?: number;
  lieferant_geschaeftspartner_id?: number;
  lieferant_firmenname?: string | null;
}

export async function linkEingangsrechnungArtikelLieferant(payload: {
  ids: number[];
  lieferant_geschaeftspartner_id: number;
  apply_to_same_lieferant_name?: boolean;
}): Promise<EingangsrechnungLinkLieferantResult> {
  return api.post<EingangsrechnungLinkLieferantResult>(
    "/invoice-mail/eingangsrechnung-artikel/link-lieferant",
    payload,
  );
}

export function einvoiceLineCountFromCandidate(row: InvoiceMailCandidate): number {
  const fromAttachments = (row.attachments_summary || []).reduce(
    (sum, a) => sum + (a.einvoice_parsed ? Number(a.einvoice_line_count || 0) : 0),
    0,
  );
  if (fromAttachments > 0) return fromAttachments;
  return 0;
}

export function stammMatchLabel(line: EingangsrechnungArtikelLine): string {
  if (!line.stamm_artikel_id) return "—";
  const name = (line.stamm_artikel_bezeichnung || "").trim();
  const hero = (line.stamm_hero_nr || "").trim();
  if (name && hero) return `${hero} – ${name}`;
  return name || hero || `#${line.stamm_artikel_id}`;
}

export function artikelMatchMethodLabel(method?: string | null): string {
  switch (method) {
    case "ean":
      return "EAN";
    case "lieferanten_artnr":
      return "Lieferanten-Artnr.";
    case "hero_nr":
      return "Hero-Nr.";
    case "name_fuzzy":
      return "Name (ähnlich)";
    case "none":
      return "Kein Treffer";
    default:
      return method || "—";
  }
}

export function eingangsrechnungLieferantGpId(row: EingangsrechnungArtikelLine): number | null {
  const id = row.lieferant_gp_id ?? row.lieferant_geschaeftspartner_id;
  return id != null && Number.isFinite(id) ? id : null;
}

export function eingangsrechnungLieferantLabel(row: EingangsrechnungArtikelLine): string {
  return (
    (row.lieferant_firmenname || "").trim() ||
    (row.lieferant_name || "").trim() ||
    (row.candidate_from_name || "").trim() ||
    "—"
  );
}

export function confidenceLabel(confidence?: string | null): string {
  switch (confidence) {
    case "exact":
      return "Exakt";
    case "partial":
      return "Teilweise";
    case "fuzzy":
      return "Ähnlich";
    default:
      return "Vorschlag";
  }
}

export function confidenceBadgeClass(confidence?: string | null): string {
  switch (confidence) {
    case "exact":
      return "bg-status-green/15 text-status-green";
    case "partial":
      return "bg-status-orange/15 text-status-orange";
    case "fuzzy":
      return "bg-accent/10 text-accent";
    default:
      return "bg-label-secondary/15 text-label-secondary";
  }
}

export async function mergeInvoiceMailAttachments(
  id: number,
  attachmentIds: string[],
): Promise<{ ok: boolean; candidate: InvoiceMailCandidate }> {
  return api.post(`/invoice-mail/candidates/${id}/merge-attachments`, {
    attachment_ids: attachmentIds,
  });
}

export async function unmergeInvoiceMailAttachments(
  id: number,
): Promise<{ ok: boolean; candidate: InvoiceMailCandidate }> {
  return api.del(`/invoice-mail/candidates/${id}/merged-attachment`);
}

export async function confirmInvoiceMailCandidate(
  id: number,
  payload: InvoiceMailConfirmPayload,
): Promise<{ ok: boolean; candidate_id: number }> {
  return api.post(`/invoice-mail/candidates/${id}/confirm`, payload);
}

export async function dismissInvoiceMailCandidate(
  id: number,
  note?: string,
): Promise<{ ok: boolean; candidate_id: number }> {
  return api.post(`/invoice-mail/candidates/${id}/dismiss`, { note: note || null });
}

export interface InvoiceMailUpdateMatchesPayload {
  targets: Array<{
    auftrag_id: number;
    bestellnummer?: string | null;
    subfolder?: string | null;
  }>;
  attachment_tks?: Record<string, string[]>;
}

export async function updateInvoiceMailMatches(
  id: number,
  payload: InvoiceMailUpdateMatchesPayload,
): Promise<{ ok: boolean; candidate: InvoiceMailCandidate }> {
  return api.patch(`/invoice-mail/candidates/${id}/matches`, payload);
}

export type LieferantEinvoiceStatus = "always" | "partial" | "none";

export interface InvoiceMailLieferantUebersichtItem {
  sender_email: string;
  from_name?: string | null;
  mail_count: number;
  pending_count: number;
  last_received_at?: string | null;
  einvoice_mail_count: number;
  lieferant_gp_id?: number | null;
  lieferant_firmenname?: string | null;
  einvoice_status: LieferantEinvoiceStatus;
}

export interface InvoiceMailLieferantUebersichtResponse {
  summary: {
    total_senders: number;
    always_einvoice: number;
    partial_einvoice: number;
    no_einvoice: number;
    needs_followup: number;
  };
  items: InvoiceMailLieferantUebersichtItem[];
}

export async function getInvoiceMailLieferantUebersicht(): Promise<InvoiceMailLieferantUebersichtResponse> {
  return api.get<InvoiceMailLieferantUebersichtResponse>("/invoice-mail/lieferanten-uebersicht");
}

export function lieferantEinvoiceStatusLabel(status: LieferantEinvoiceStatus): string {
  switch (status) {
    case "always":
      return "E-Rechnung";
    case "partial":
      return "Teilweise";
    case "none":
      return "Keine E-Rechnung";
    default:
      return status;
  }
}

export function lieferantEinvoiceStatusClass(status: LieferantEinvoiceStatus): string {
  switch (status) {
    case "always":
      return "bg-status-green/15 text-status-green";
    case "partial":
      return "bg-status-orange/15 text-status-orange";
    case "none":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-label-secondary/15 text-label-secondary";
  }
}

export function invoiceMailAttachmentUrl(candidateId: number, attachmentId: string): string {
  return `${API_BASE}/invoice-mail/candidates/${candidateId}/attachments/${encodeURIComponent(attachmentId)}`;
}

export async function fetchInvoiceMailAttachment(
  candidateId: number,
  attachmentId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const res = await fetch(invoiceMailAttachmentUrl(candidateId, attachmentId), {
    credentials: "include",
    cache: "no-store",
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export interface InvoiceMailOrderHint {
  order_id: number;
  mail_count: number;
  latest_received_at?: string | null;
  has_pending?: boolean;
  has_confirmed?: boolean;
}

export async function getInvoiceMailOrderHints(
  orderIds: number[],
): Promise<Record<number, InvoiceMailOrderHint>> {
  if (!orderIds.length) return {};
  const q = orderIds.join(",");
  const res = await api.get<{ hints: Record<string, InvoiceMailOrderHint> }>(
    `/invoice-mail/order-hints?order_ids=${encodeURIComponent(q)}`,
  );
  const out: Record<number, InvoiceMailOrderHint> = {};
  for (const [key, hint] of Object.entries(res.hints || {})) {
    const id = Number(key);
    if (Number.isFinite(id)) out[id] = hint;
  }
  return out;
}

export async function listInvoiceMailForOrder(orderId: number): Promise<InvoiceMailCandidate[]> {
  const res = await api.get<{ order_id: number; items: InvoiceMailCandidate[] }>(
    `/invoice-mail/by-order/${orderId}`,
  );
  return res.items || [];
}

export function invoiceMailStatusLabel(status: InvoiceMailStatus): string {
  switch (status) {
    case "pending":
      return "Im Postfach";
    case "confirmed":
      return "Abgelegt";
    case "dismissed":
      return "Verworfen";
    case "skipped":
      return "Ohne Anhang";
    default:
      return status;
  }
}

export function matchReasonLabel(reason: string): string {
  switch (reason) {
    case "tk_in_subject":
      return "Bestellnummer im Betreff";
    case "tk_in_body":
      return "Bestellnummer im Text";
    case "tk_in_attachment_name":
      return "Bestellnummer im Dateinamen";
    case "tk_in_attachment_content":
      return "Bestellnummer im Anhang-Inhalt";
    case "tk_in_attachment_pdf":
      return "Bestellnummer in PDF-Anhang";
    case "tk_exact":
      return "Exakte Bestellnummer";
    case "tk_partial":
      return "Teilweise Bestellnummer";
    case "tk_fuzzy":
      return "Ähnliche Bestellnummer";
    default:
      return reason;
  }
}
